import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Cpu, Database, HardDrive, MemoryStick, Server } from 'lucide-react';
import type { ServerMetrics } from '@simpleacs/shared';
import { api } from '../../lib/api';
import { formatBytes, formatUptime } from '../../lib/format';
import { Card, ErrorNotice, Spinner } from '../../components/ui';

/**
 * The SimpleACS server's own CPU, memory and disk — the superadmin's half of the
 * dashboard, where an org user sees the OLT fleet.
 *
 * The fleet table answers "is the network up", which every operator can already
 * see from the four stat cards above it. Only the superadmin can do anything
 * about the server the whole platform runs on, and until now nothing in the UI
 * said the disk was filling.
 *
 * Each row is a proportion, never a bare number: "44 MB" says nothing, "44 MB of
 * 40 GB" says everything. A filesystem at 94% has to read as alarming without
 * being read at all, so the bar carries the warning and the text merely confirms
 * it.
 *
 * Anything the host cannot honestly answer arrives null with a note, and the row
 * shows the note instead of a fabricated figure — Windows has no load average,
 * and a remote database has no size this server can measure.
 */

/** Red before it is too late to act, amber while there is still time. */
function toneFor(percent: number): 'ok' | 'warn' | 'danger' {
  if (percent >= 90) return 'danger';
  if (percent >= 75) return 'warn';
  return 'ok';
}

const BAR = { ok: 'bg-primary', warn: 'bg-warn', danger: 'bg-danger' } as const;
const TEXT = { ok: 'text-ink', warn: 'text-warn', danger: 'text-danger' } as const;

function Meter({
  icon,
  label,
  used,
  total,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  /** Null when this host cannot measure it; the bar then stays empty and `hint` explains. */
  used: number | null;
  total: number | null;
  hint?: string | null;
}) {
  const known = used !== null && total !== null && total > 0;
  const percent = known ? Math.min(100, Math.max(0, (used / total) * 100)) : 0;
  const tone = known ? toneFor(percent) : 'ok';

  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <span className="flex items-center gap-2 text-sm text-muted">
          <span className="text-faint">{icon}</span>
          {label}
        </span>
        <span className={clsx('text-sm font-medium tabular-nums', TEXT[tone])}>
          {known ? (
            <>
              {formatBytes(used)}
              <span className="text-faint"> / {formatBytes(total)}</span>
              <span className="ml-2 text-xs text-faint">{Math.round(percent)}%</span>
            </>
          ) : (
            <span className="text-faint">not measurable here</span>
          )}
        </span>
      </div>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-surface-3">
        {known && (
          <div className={clsx('h-full rounded-full', BAR[tone])} style={{ width: `${percent}%` }} />
        )}
      </div>
      {hint && <div className="mt-1 text-xs text-faint">{hint}</div>}
    </div>
  );
}

/** CPU is a percentage of time, not of bytes, so it gets its own row shape. */
function CpuMeter({ cpu }: { cpu: ServerMetrics['cpu'] }) {
  const known = cpu.usagePercent !== null;
  const percent = cpu.usagePercent ?? 0;
  const tone = known ? toneFor(percent) : 'ok';

  const parts: string[] = [`${cpu.cores} core${cpu.cores === 1 ? '' : 's'}`];
  if (cpu.load) {
    parts.push(
      `load ${cpu.load.map((n) => n.toFixed(2)).join(' / ')}${
        cpu.loadPerCore === null ? '' : ` · ${cpu.loadPerCore.toFixed(2)} per core`
      }`,
    );
  }
  // The window is worth saying: a percentage without one is the number people
  // wrongly assume os.cpus() gives on its own.
  if (cpu.sampleMs !== null) parts.push(`over ${(cpu.sampleMs / 1000).toFixed(1)}s`);
  if (cpu.note) parts.push(cpu.note);

  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <span className="flex items-center gap-2 text-sm text-muted">
          <span className="text-faint">
            <Cpu className="size-4" />
          </span>
          CPU
        </span>
        <span className={clsx('text-sm font-medium tabular-nums', TEXT[tone])}>
          {known ? `${percent.toFixed(1)}%` : <span className="text-faint">sampling…</span>}
        </span>
      </div>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-surface-3">
        {known && (
          <div className={clsx('h-full rounded-full', BAR[tone])} style={{ width: `${percent}%` }} />
        )}
      </div>
      <div className="mt-1 truncate text-xs text-faint" title={cpu.model}>
        {parts.join(' · ')}
      </div>
    </div>
  );
}

export function ServerResourcesCard() {
  /**
   * Five seconds, and only while this page is actually being looked at.
   *
   * "As long as we are on that page" is two separate conditions, and TanStack
   * enforces both without help: the interval callback checks `focusManager`
   * before it fetches, so a backgrounded tab keeps its timer but never issues a
   * request; and unmounting the card — navigating off the dashboard — takes the
   * last listener off the observer, which destroys it and clears the interval
   * outright. A dashboard left open overnight on a hidden tab is silent, and a
   * dashboard navigated away from stops having a timer at all.
   *
   * The global default is `refetchOnWindowFocus: false`, which is right for
   * every other query on this page and wrong for this one: coming back to the
   * tab would otherwise show a figure up to five seconds stale on a card whose
   * whole claim is that it is live. Overridden here, and only here.
   *
   * Three times the rate costs nothing that can be queued behind: the endpoint
   * makes no chassis contact, opens no CLI session and takes no write lock. It
   * is `os` counters, one `/proc/meminfo` read, two `statfs` calls and three
   * `stat` calls — and the file-store directory walk stays cached for 60s on the
   * server, so a 5s poll walks a directory once every twelfth request, not every
   * request.
   */
  const query = useQuery({
    queryKey: ['server-metrics'],
    queryFn: () => api.get<ServerMetrics>('/infra/server'),
    refetchInterval: 5_000,
    refetchOnWindowFocus: true,
  });
  const m = query.data;

  return (
    <Card
      title="Server resources"
      action={
        m ? (
          <span className="truncate text-xs text-faint" title={`${m.host.platform} ${m.host.release}`}>
            {m.host.hostname} · up {formatUptime(m.host.uptimeSeconds)}
          </span>
        ) : null
      }
    >
      {query.isLoading ? (
        <div className="flex justify-center p-8">
          <Spinner />
        </div>
      ) : query.error ? (
        <div className="p-5">
          <ErrorNotice error={query.error} />
        </div>
      ) : m ? (
        <div className="space-y-4 px-5 py-4">
          <CpuMeter cpu={m.cpu} />

          <Meter
            icon={<MemoryStick className="size-4" />}
            label="Memory"
            used={m.memory.usedBytes}
            total={m.memory.totalBytes}
            hint={[
              `${formatBytes(m.memory.availableBytes)} available`,
              m.memory.swap && m.memory.swap.totalBytes > 0
                ? `swap ${formatBytes(m.memory.swap.usedBytes)} / ${formatBytes(m.memory.swap.totalBytes)}`
                : null,
              // Says which number this is, because "free" and "available" differ
              // by the whole page cache on a busy Linux box.
              m.memory.note ?? 'MemAvailable, cache and buffers counted as reclaimable',
            ]
              .filter(Boolean)
              .join(' · ')}
          />

          {m.disks.map((disk) => (
            <Meter
              key={`${disk.label}:${disk.path}`}
              icon={<HardDrive className="size-4" />}
              label={disk.label === 'Database + File store' ? 'Storage' : `Storage — ${disk.label}`}
              used={disk.usedBytes}
              total={disk.totalBytes}
              hint={`${formatBytes(disk.freeBytes)} free · ${disk.path}`}
            />
          ))}
          {m.diskNote && <p className="text-xs text-warn">{m.diskNote}</p>}

          {/* What is actually using it — the operator's live question while the
              OLT-backup migration is in flight. */}
          <div className="grid grid-cols-2 gap-3 border-t border-line pt-3 text-xs">
            <div className="flex items-center gap-2 text-faint" title={m.database.path}>
              <Database className="size-3.5 shrink-0" />
              <span className="truncate">
                Database{' '}
                <span className="font-medium tabular-nums text-muted">
                  {formatBytes(m.database.bytes)}
                </span>
              </span>
            </div>
            <div
              className="flex items-center gap-2 text-faint"
              title={m.fileStore.note ?? m.fileStore.path ?? m.fileStore.kind}
            >
              <Server className="size-3.5 shrink-0" />
              <span className="truncate">
                File store{' '}
                <span className="font-medium tabular-nums text-muted">
                  {m.fileStore.bytes === null
                    ? m.fileStore.kind
                    : `${m.fileStore.truncated ? '≥ ' : ''}${formatBytes(m.fileStore.bytes)}`}
                </span>
              </span>
            </div>
          </div>

          {/*
            PostgreSQL, when that is what is behind it.

            Four numbers rather than a wall of them, chosen because each one
            answers a question the operator would otherwise SSH in to ask:
            connections against the ceiling (the platform holds a pool and the
            sweeps open more under load), cache hit rate (a working set this
            size should live in memory; a sagging figure means something is
            scanning), and the oldest open transaction — the number that
            explains a database which has quietly stopped reclaiming space,
            because one forgotten transaction holds the horizon and every
            autovacuum after it does nothing.
          */}
          {m.database.postgres && (
            <div className="border-t border-line pt-3">
              <div className="mb-2 flex items-center gap-2 text-xs text-faint">
                <Database className="size-3.5 shrink-0" />
                <span>
                  PostgreSQL{' '}
                  <span className="font-medium text-muted">{m.database.postgres.version}</span>
                  {m.database.postgres.liveRows !== null && (
                    <span className="text-faint">
                      {' · '}
                      <span className="tabular-nums">
                        {m.database.postgres.liveRows.toLocaleString()}
                      </span>{' '}
                      rows
                    </span>
                  )}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-3 text-xs">
                <Stat
                  label="Connections"
                  value={`${m.database.postgres.connectionsUsed} / ${m.database.postgres.connectionsMax}`}
                  tone={connectionTone(
                    m.database.postgres.connectionsUsed,
                    m.database.postgres.connectionsMax,
                  )}
                />
                <Stat
                  label="Cache hit"
                  value={
                    m.database.postgres.cacheHitPercent === null
                      ? 'not yet'
                      : `${m.database.postgres.cacheHitPercent}%`
                  }
                  tone={
                    m.database.postgres.cacheHitPercent === null
                      ? 'ok'
                      : m.database.postgres.cacheHitPercent < 95
                        ? 'warn'
                        : 'ok'
                  }
                />
                <Stat
                  label="Oldest txn"
                  value={formatAge(m.database.postgres.oldestTransactionSec)}
                  tone={
                    (m.database.postgres.oldestTransactionSec ?? 0) > 300 ? 'warn' : 'ok'
                  }
                />
              </div>
            </div>
          )}
        </div>
      ) : null}
    </Card>
  );
}

/** Connections are a ceiling you hit, not a gauge you watch — warn with room to act. */
function connectionTone(used: number, max: number): 'ok' | 'warn' | 'danger' {
  const percent = max > 0 ? (used / max) * 100 : 0;
  return toneFor(percent);
}

/** "none" is the right answer for no open transaction, and it is the common one. */
function formatAge(seconds: number | null): string {
  if (seconds === null) return 'none';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${(seconds / 3600).toFixed(1)}h`;
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'ok' | 'warn' | 'danger';
}) {
  return (
    <div>
      <div className="text-faint">{label}</div>
      <div className={`font-medium tabular-nums ${TEXT[tone]}`}>{value}</div>
    </div>
  );
}
